import React, { useState } from 'react';

import { Loader, Box } from '@opendoor/bricks/core';

import { IScheduleCardProps } from 'shared/components/overview/ScheduledEvent';
import { EventList, EventListProps } from 'shared/components/overview/EventList';
import { BREAKPOINTS_DICT } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';
import { ORVAReschedule } from 'sellDirect/components/homeWalkthrough/ORVAReschedule';
import { Modal } from '@opendoor/bricks/complex';
import { CalendlySchedulingModal } from 'sellDirect/components/legacy/sellerDash/CalendlySchedulingModal';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { getLeadInfo } from 'sellDirect/helpers/useCalendlyParams';
import { FinalizingOfferContent } from 'sellDirect/components/offer/FinalizingOfferContent';
import { getPreClosingOffer, ORVAData, SSVAData, useOfferContext } from 'sellDirect/OfferContext';
import { ZonedDateTime } from '@js-joda/core';

interface SellDirectEventsWithoutContextProps {
  shouldSeeAgentExperience: boolean;
  offerReviewAt?: ZonedDateTime;
  orvaData?: ORVAData;
  ssvaData?: SSVAData;
  finalizingOffer?: boolean;
  phone?: string;
  isOfferContextLoading: boolean;
}

/**
 * Displays (if any) the current Sell Direct event
 *
 * storybook-check-ignore
 */
export const SellDirectEventsWithoutContext: React.FC<SellDirectEventsWithoutContextProps> = ({
  shouldSeeAgentExperience,
  isOfferContextLoading,
  orvaData,
  ssvaData,
  finalizingOffer,
  phone,
}) => {
  const [openORVAModal, setOpenORVAModal] = useState<boolean>(false);
  const [openFinalizingOfferModal, setOpenFinalizingOfferModal] = useState<boolean>(false);
  const [calendlyModalOpen, setCalendlyModalOpen] = useState<boolean>(false);

  const scheduledEvents: IScheduleCardProps[] = [];

  if (orvaData?.offerReviewAt && !finalizingOffer) {
    if (orvaData.offerReviewAt.isAfter(ZonedDateTime.now())) {
      scheduledEvents.push({
        title: 'Video walkthrough',
        analyticsName: 'scheduled-events.virtual-assessment',
        /** TODO: Add documentation */
        onClick: () => setOpenORVAModal(true),
        scheduleTime: orvaData.offerReviewAt,
        eventDuration: 30,
      });
    }
  }

  const otherEvents: EventListProps['otherEvents'] = [];

  if (finalizingOffer) {
    otherEvents.push({
      /** TODO: Add documentation */
      onClick: () => setOpenFinalizingOfferModal(true),
      analyticsName: 'finalizing-offer-card',
      chipOrIconProps: { firstLine: '2-4', secondLine: 'DAYS' },
      title: `We’re finalizing ${shouldSeeAgentExperience ? 'your client’s' : 'your'} offer`,
    });
  }

  if (isOfferContextLoading || orvaData?.refetching) {
    return <Loader data-testid="loader" />;
  }

  if (scheduledEvents.length === 0 && otherEvents.length === 0) {
    return null;
  }

  return (
    <>
      <Box width="100%" mx="auto" maxWidth={[null, null, BREAKPOINTS_DICT.MD]}>
        <EventList
          data-testid="scheduled-events"
          scheduledEvents={scheduledEvents.sort((a, b) => a.scheduleTime.compareTo(b.scheduleTime))}
          otherEvents={otherEvents}
        />
      </Box>
      {ssvaData && orvaData && (
        <Modal
          analyticsName="cake-connected-events-orva-schedule-modal"
          isOpen={openORVAModal}
          showClose
          onRequestClose={() => setOpenORVAModal(false)}
          headerProps={{ title: 'Video walkthrough', titleProps: { as: 'h2' } }}
        >
          <ORVAReschedule
            onReschedule={() => {
              setCalendlyModalOpen(true);
              setOpenORVAModal(false);
            }}
            ssvaState={ssvaData.ssvaState}
            offerReviewAt={orvaData.offerReviewAt}
          />
        </Modal>
      )}
      <Modal
        analyticsName="cake-connected-events-finalizing-offer-modal"
        isOpen={openFinalizingOfferModal}
        showClose
        onRequestClose={() => setOpenFinalizingOfferModal(false)}
        headerProps={{ title: 'We’re finalizing your offer', titleProps: { as: 'h2' } }}
      >
        <FinalizingOfferContent />
      </Modal>
      {orvaData && (
        <CalendlySchedulingModal
          isReschedule
          overridePhoneNumber={phone}
          isOpen={calendlyModalOpen}
          onRequestClose={() => {
            setCalendlyModalOpen(false);
          }}
          analyticsName="cake-connected-events-calendly-scheduling-modal"
          aria-label="Schedule appointment"
          orvaData={orvaData}
        />
      )}
    </>
  );
};

/**
 * Displays events related to Sell Direct.
 * NOTE: This is a copy of the shared ConnectedEvents since that file has a lot of technical debt.
 * @todo: Refactor the current Connected Events and this component.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const SellDirectEvents: React.FC = () => {
  const { lead } = useQualificationContext();
  const { offer, shouldSeeAgentExperience, loading } = useOfferContext();

  const preClosingOffer = getPreClosingOffer(offer);
  const { phone } = getLeadInfo(lead);
  const finalizingOffer = offer?.state === 'Finalizing' && !offer?.isExpired;

  return (
    <SellDirectEventsWithoutContext
      shouldSeeAgentExperience={shouldSeeAgentExperience}
      phone={phone}
      finalizingOffer={finalizingOffer}
      orvaData={preClosingOffer?.orvaData}
      ssvaData={preClosingOffer?.ssvaData}
      isOfferContextLoading={loading}
    />
  );
};
